// src/lib/jumpLayout.ts
/**
 * Phase 2: Jump Indicator Layout Fix - Layout Utilities
 * 
 * Implements:
 * - Sequential jump chain detection
 * - Horizontal side-by-side layout for chains
 * - Vertical lane stacking for non-chained jumps
 * 
 * Based on DNL Enhancement Specification v1.0, Section 3.3
 */

import type {
  Jump,
  JumpChain,
  JumpLayoutResult,
  JumpLayoutSettings,
  DEFAULT_JUMP_LAYOUT_SETTINGS,
} from '../types/jump';
import { approximate, formatFractionForMath, formatFraction, type Fraction } from './fractions';
import { formatNumberWithPrecision, type LocaleCode } from './locale';

// =============================================================================
// CHAIN DETECTION
// =============================================================================

/**
 * Determines if two jumps are sequential (end of first = start of second)
 * Sequential jumps can be rendered side-by-side in a chain
 * 
 * @param jump1 - First jump (should end where jump2 starts)
 * @param jump2 - Second jump (should start where jump1 ends)
 * @param tolerance - Maximum difference to consider values equal
 * @returns True if jumps are sequential
 */
export function areJumpsSequential(
  jump1: Jump,
  jump2: Jump,
  tolerance: number = 0.001
): boolean {
  // Sequential means: end of jump1 equals start of jump2
  // Both jumps must be on the same line
  if (jump1.line !== jump2.line) {
    return false;
  }
  
  return Math.abs(jump1.endValue - jump2.startValue) < tolerance;
}

/**
 * Groups jumps into chains of sequential jumps
 * Jumps in a chain will be rendered side-by-side horizontally
 * 
 * @param jumps - Array of jumps to analyze
 * @param tolerance - Tolerance for sequential detection
 * @returns Array of jump chains (single-jump chains for non-sequential jumps)
 */
export function detectJumpChains(
  jumps: Jump[],
  tolerance: number = 0.001
): Jump[][] {
  if (jumps.length === 0) return [];
  
  // Sort jumps by start value on each line
  const sortedJumps = [...jumps].sort((a, b) => {
    // First sort by line
    if (a.line !== b.line) {
      return a.line === 'primary' ? -1 : 1;
    }
    // Then by start value
    return a.startValue - b.startValue;
  });
  
  const chains: Jump[][] = [];
  let currentChain: Jump[] = [];
  
  for (let i = 0; i < sortedJumps.length; i++) {
    const currentJump = sortedJumps[i];
    
    if (currentChain.length === 0) {
      // Start a new chain
      currentChain.push(currentJump);
    } else {
      const lastJump = currentChain[currentChain.length - 1];
      
      // Check if this jump continues the chain
      if (areJumpsSequential(lastJump, currentJump, tolerance)) {
        currentChain.push(currentJump);
      } else {
        // End current chain and start a new one
        chains.push(currentChain);
        currentChain = [currentJump];
      }
    }
  }
  
  // Don't forget the last chain
  if (currentChain.length > 0) {
    chains.push(currentChain);
  }
  
  return chains;
}

// =============================================================================
// LANE CALCULATION
// =============================================================================

interface JumpInterval {
  jump: Jump;
  startPx: number;
  endPx: number;
  width: number;
  chainId?: string;
}

/**
 * Calculates vertical lanes for jumps to avoid overlapping
 * 
 * When chainSequentialJumps is enabled:
 * - Sequential chains are treated as single units for lane assignment
 * - All jumps in a chain share the same lane
 * 
 * When disabled:
 * - Traditional behavior: each jump gets its own lane assignment
 * 
 * @param jumps - Array of jumps to layout
 * @param valueToPixel - Function to convert values to pixel positions
 * @param settings - Layout configuration
 * @returns Layout result with lane assignments and chain info
 */
export function calculateJumpLayout(
  jumps: Jump[],
  valueToPixel: (value: number) => number,
  settings: JumpLayoutSettings = DEFAULT_JUMP_LAYOUT_SETTINGS
): JumpLayoutResult {
  if (jumps.length === 0) {
    return { jumpsWithLanes: [], chains: [], maxLane: 0 };
  }
  
  const { chainSequentialJumps, minLaneSeparation, chainTolerance } = settings;
  
  // Step 1: Detect chains if enabled
  const chainGroups = chainSequentialJumps
    ? detectJumpChains(jumps, chainTolerance)
    : jumps.map(j => [j]); // Each jump is its own "chain"
  
  // Step 2: Convert to intervals for lane calculation
  const intervals: JumpInterval[] = [];
  const chainMap = new Map<string, JumpChain>();
  
  chainGroups.forEach((chain, chainIndex) => {
    const chainId = `chain-${chainIndex}`;
    
    // Calculate overall chain bounds
    const chainStartPx = Math.min(
      ...chain.map(j => Math.min(valueToPixel(j.startValue), valueToPixel(j.endValue)))
    );
    const chainEndPx = Math.max(
      ...chain.map(j => Math.max(valueToPixel(j.startValue), valueToPixel(j.endValue)))
    );
    
    // For lane calculation, treat the entire chain as one unit
    if (chainSequentialJumps && chain.length > 1) {
      // Multi-jump chain: one interval for the whole chain
      intervals.push({
        jump: chain[0], // Representative jump (for line info)
        startPx: chainStartPx,
        endPx: chainEndPx,
        width: chainEndPx - chainStartPx,
        chainId,
      });
    } else {
      // Single jump or chaining disabled: individual intervals
      chain.forEach(jump => {
        const startPx = Math.min(valueToPixel(jump.startValue), valueToPixel(jump.endValue));
        const endPx = Math.max(valueToPixel(jump.startValue), valueToPixel(jump.endValue));
        intervals.push({
          jump,
          startPx,
          endPx,
          width: endPx - startPx,
          chainId: chain.length > 1 ? chainId : undefined,
        });
      });
    }
    
    // Record chain info
    if (chain.length > 1) {
      chainMap.set(chainId, {
        id: chainId,
        jumps: chain,
        lane: 0, // Will be set during lane assignment
        startPx: chainStartPx,
        endPx: chainEndPx,
      });
    }
  });
  
  // Step 3: Sort intervals by start position
  intervals.sort((a, b) => a.startPx - b.startPx);
  
  // Step 4: Greedy lane assignment
  const lanes: Array<{ endPx: number; intervals: JumpInterval[] }> = [];
  const laneAssignments = new Map<string, number>(); // jumpId -> lane
  const chainLaneAssignments = new Map<string, number>(); // chainId -> lane
  
  for (const interval of intervals) {
    let assignedLane = -1;
    
    // Find first lane where this interval fits
    for (let laneIndex = 0; laneIndex < lanes.length; laneIndex++) {
      const lane = lanes[laneIndex];
      if (interval.startPx >= lane.endPx + minLaneSeparation) {
        assignedLane = laneIndex;
        break;
      }
    }
    
    // Create new lane if needed
    if (assignedLane === -1) {
      assignedLane = lanes.length;
      lanes.push({ endPx: 0, intervals: [] });
    }
    
    // Assign to lane
    lanes[assignedLane].endPx = interval.endPx;
    lanes[assignedLane].intervals.push(interval);
    
    // Record assignment
    if (interval.chainId) {
      chainLaneAssignments.set(interval.chainId, assignedLane);
    }
    laneAssignments.set(interval.jump.id, assignedLane);
  }
  
  // Step 5: Build result
  const jumpsWithLanes: (Jump & { lane: number })[] = [];
  
  chainGroups.forEach((chain, chainIndex) => {
    const chainId = `chain-${chainIndex}`;
    
    if (chainSequentialJumps && chain.length > 1) {
      // All jumps in chain share the chain's lane
      const chainLane = chainLaneAssignments.get(chainId) ?? 0;
      chain.forEach(jump => {
        jumpsWithLanes.push({ ...jump, lane: chainLane });
      });
      
      // Update chain lane
      const chainInfo = chainMap.get(chainId);
      if (chainInfo) {
        chainInfo.lane = chainLane;
      }
    } else {
      // Individual lane assignments
      chain.forEach(jump => {
        const lane = laneAssignments.get(jump.id) ?? 0;
        jumpsWithLanes.push({ ...jump, lane });
      });
    }
  });
  
  const maxLane = Math.max(0, ...jumpsWithLanes.map(j => j.lane));
  
  return {
    jumpsWithLanes,
    chains: Array.from(chainMap.values()),
    maxLane,
  };
}

// =============================================================================
// LEGACY COMPATIBILITY
// =============================================================================

/**
 * Legacy function for calculating jump lanes (backward compatible)
 * Wraps the new calculateJumpLayout function
 * 
 * @deprecated Use calculateJumpLayout instead for full chain support
 */
export function calculateJumpLanes(
  jumps: Jump[],
  valueToPixel: (value: number) => number,
  minSeparation: number = 40
): (Jump & { lane: number })[] {
  const settings: JumpLayoutSettings = {
    ...DEFAULT_JUMP_LAYOUT_SETTINGS,
    minLaneSeparation: minSeparation,
    chainSequentialJumps: true, // Enable by default
  };
  
  const result = calculateJumpLayout(jumps, valueToPixel, settings);
  return result.jumpsWithLanes;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Gets the vertical offset for a given lane
 * 
 * @param lane - Lane number (0 = closest to line)
 * @param laneOffset - Pixels per lane
 * @returns Pixel offset from base position
 */
export function getLaneOffset(lane: number, laneOffset: number = 25): number {
  return lane * laneOffset;
}

/**
 * Determines if a jump is part of a chain with more than one jump
 * 
 * @param jump - Jump to check
 * @param chains - All detected chains
 * @returns True if jump is in a multi-jump chain
 */
export function isJumpInChain(jump: Jump, chains: JumpChain[]): boolean {
  return chains.some(chain => 
    chain.jumps.length > 1 && 
    chain.jumps.some(j => j.id === jump.id)
  );
}

/**
 * Gets the chain containing a specific jump
 * 
 * @param jump - Jump to find
 * @param chains - All detected chains
 * @returns Chain containing the jump, or undefined
 */
export function getJumpChain(jump: Jump, chains: JumpChain[]): JumpChain | undefined {
  return chains.find(chain => chain.jumps.some(j => j.id === jump.id));
}

/**
 * Gets the position of a jump within its chain
 * 
 * @param jump - Jump to find
 * @param chains - All detected chains
 * @returns Object with chainIndex and positionInChain, or undefined
 */
export function getJumpPositionInChain(
  jump: Jump,
  chains: JumpChain[]
): { chainIndex: number; positionInChain: number; chainLength: number } | undefined {
  for (let chainIndex = 0; chainIndex < chains.length; chainIndex++) {
    const chain = chains[chainIndex];
    const positionInChain = chain.jumps.findIndex(j => j.id === jump.id);
    if (positionInChain !== -1) {
      return {
        chainIndex,
        positionInChain,
        chainLength: chain.jumps.length,
      };
    }
  }
  return undefined;
}

// =============================================================================
// LABEL FORMATTING
// =============================================================================

/**
 * Formats a number with sign according to settings
 * @param forKaTeX - If true, formats fractions as LaTeX; if false, formats as plain text
 * @param superscriptSign - If true, makes the sign superscript (for LaTeX)
 */
function formatNumberWithSign(
  value: number,
  alwaysShowSigns: boolean,
  numberType: 'integer' | 'decimal' | 'fraction' | 'percent',
  locale: LocaleCode,
  fractionDisplay?: { format: 'mixed' | 'improper'; simplify: boolean },
  forKaTeX: boolean = false,
  superscriptSign: boolean = false
): string {
  if (Math.abs(value) < 1e-10) {
    if (alwaysShowSigns) {
      return superscriptSign && forKaTeX ? '^{+}0' : '+0';
    }
    return '0';
  }

  let formatted: string;
  
  switch (numberType) {
    case 'integer':
      formatted = Math.round(value).toString();
      break;
    case 'decimal':
      formatted = formatNumberWithPrecision(value, locale, 4);
      break;
    case 'fraction': {
      const maxDen = fractionDisplay?.simplify !== false ? 100 : 1000;
      const fraction = approximate(value, maxDen);
      if (forKaTeX) {
        formatted = formatFractionForMath(fraction, { mixed: fractionDisplay?.format === 'mixed' });
      } else {
        formatted = formatFraction(fraction, { 
          mixed: fractionDisplay?.format === 'mixed',
          simplify: fractionDisplay?.simplify !== false,
          locale 
        });
      }
      break;
    }
    case 'percent':
      formatted = formatNumberWithPrecision(value * 100, locale, 1) + '%';
      break;
    default:
      formatted = formatNumberWithPrecision(value, locale, 4);
  }

  if (alwaysShowSigns && value > 0) {
    if (superscriptSign && forKaTeX) {
      // Use LaTeX superscript: put + as superscript before the number
      // Format: {}^{+}number or use prescript
      return `{}^{+}${formatted}`;
    }
    return `+${formatted}`;
  }
  
  if (value < 0) {
    if (superscriptSign && forKaTeX) {
      // For negative, extract the absolute value and put - as superscript
      const absFormatted = formatted.replace(/^-/, '');
      return `{}^{-}${absFormatted}`;
    }
    // Negative value - sign is already in formatted string
  }
  
  return formatted;
}

/**
 * Formats a number as a fraction if possible, otherwise as decimal
 * Used for jump span labels (legacy function, kept for backward compatibility)
 */
export function formatJumpSpan(span: number): string {
  if (span === 0) return '0';
  
  const sign = span >= 0 ? '+' : '';
  const absSpan = Math.abs(span);
  
  // Check if it's a simple integer
  if (Number.isInteger(absSpan)) {
    return `${sign}${span}`;
  }
  
  // Try common fractions
  const fraction = decimalToFraction(absSpan);
  if (fraction) {
    const fractionStr = fraction.d === 1 
      ? `${fraction.n}` 
      : `${fraction.n}/${fraction.d}`;
    return span < 0 ? `-${fractionStr}` : `+${fractionStr}`;
  }
  
  // Fallback to decimal
  const formatted = absSpan.toFixed(absSpan < 1 ? 3 : absSpan < 10 ? 2 : 1);
  return `${sign}${span < 0 ? '-' : ''}${formatted}`;
}

/**
 * Convert decimal to simple fraction with max denominator 16
 */
function decimalToFraction(decimal: number): { n: number; d: number } | null {
  if (Number.isInteger(decimal)) {
    return { n: decimal, d: 1 };
  }
  
  const commonFractions: Array<{ decimal: number; n: number; d: number }> = [
    { decimal: 0.5, n: 1, d: 2 },
    { decimal: 0.25, n: 1, d: 4 },
    { decimal: 0.75, n: 3, d: 4 },
    { decimal: 0.333333, n: 1, d: 3 },
    { decimal: 0.666667, n: 2, d: 3 },
    { decimal: 0.125, n: 1, d: 8 },
    { decimal: 0.375, n: 3, d: 8 },
    { decimal: 0.625, n: 5, d: 8 },
    { decimal: 0.875, n: 7, d: 8 },
    { decimal: 0.2, n: 1, d: 5 },
    { decimal: 0.4, n: 2, d: 5 },
    { decimal: 0.6, n: 3, d: 5 },
    { decimal: 0.8, n: 4, d: 5 },
  ];
  
  const wholePart = Math.floor(decimal);
  const fractionalPart = decimal - wholePart;
  
  for (const frac of commonFractions) {
    if (Math.abs(fractionalPart - frac.decimal) < 0.001) {
      return { n: wholePart * frac.d + frac.n, d: frac.d };
    }
  }
  
  // Brute force for small denominators
  for (let d = 2; d <= 16; d++) {
    const n = Math.round(decimal * d);
    if (Math.abs(decimal - n / d) < 0.001) {
      return { n, d };
    }
  }
  
  return null;
}

/**
 * Formats a jump label for KaTeX mathematical rendering
 */
export function formatJumpLabelForKaTeX(
  jump: Jump,
  settings: JumpLayoutSettings,
  numberType: 'integer' | 'decimal' | 'fraction' | 'percent',
  locale: LocaleCode,
  fractionDisplay?: { format: 'mixed' | 'improper'; simplify: boolean }
): string {
  // If custom label is set and auto-label is disabled, use it
  if (!jump.autoLabel && jump.label) {
    return jump.label;
  }

  const { jumpType, scaleFactorFormat, showCalculation, showResult, showJumpValue, alwaysShowSigns } = settings;
  const startValue = jump.startValue;
  const endValue = jump.endValue;
  const span = endValue - startValue;

  // Check if multiplicative mode is valid (start value must not be zero)
  const canUseMultiplicative = jumpType === 'multiplicative' && Math.abs(startValue) > 1e-10;
  const effectiveJumpType = canUseMultiplicative ? 'multiplicative' : 'additive';

  if (effectiveJumpType === 'multiplicative') {
    // Multiplicative mode: startValue × factor = endValue
    // Calculate the scale factor: factor = endValue / startValue
    const factor = endValue / startValue;
    
    // Format scale factor with sufficient precision to avoid misleading rounding
    // For scale factors, always use decimal or fraction format (not integer) to preserve accuracy
    // If numberType is integer, use decimal instead for the factor to show exact value
    const factorNumberType = numberType === 'integer' ? 'decimal' : numberType;
    const factorStr = formatNumberWithSign(factor, false, factorNumberType, locale, fractionDisplay, true, false);
    // Format start and end values with signs if requested
    const startStr = formatNumberWithSign(startValue, alwaysShowSigns, numberType, locale, fractionDisplay, true, true);
    const endStr = formatNumberWithSign(endValue, alwaysShowSigns, numberType, locale, fractionDisplay, true, true);
    
    // Build the label based on toggles
    if (scaleFactorFormat === 'SF=') {
      // SF= format: show "SF=3" and optionally "1×3=3" on separate line
      const parts: string[] = [];
      
      // Show jump value (SF=3) if toggle is on
      if (showJumpValue) {
        parts.push(`SF=${factorStr}`);
      }
      
      // Show calculation if toggle is on
      if (showCalculation) {
        const calcLine = `${startStr}\\times${factorStr}${showResult ? `=${endStr}` : ''}`;
        if (showJumpValue) {
          // If jump value is shown, put calculation on new line
          parts.push(`\\\\${calcLine}`);
        } else {
          parts.push(calcLine);
        }
      } else if (showResult && !showJumpValue) {
        // Only show result if calculation is off and jump value is off
        parts.push(`=${endStr}`);
      }
      
      return parts.join('');
    } else {
      // × format: show "×4" or "1×4=6" or just parts
      const parts: string[] = [];
      
      // Show jump value (×4) if toggle is on and calculation is off
      if (showJumpValue && !showCalculation) {
        parts.push(`\\times${factorStr}`);
      }
      
      // Show calculation if toggle is on
      if (showCalculation) {
        parts.push(`${startStr}\\times${factorStr}`);
      }
      
      // Show result if toggle is on
      if (showResult) {
        if (showCalculation || showJumpValue) {
          parts.push('=');
        }
        parts.push(endStr);
      }
      
      return parts.join(' ');
    }
  } else {
    // Additive mode: startValue + span = endValue
    // Format with LaTeX for KaTeX rendering, with superscript signs
    const startStr = formatNumberWithSign(startValue, alwaysShowSigns, numberType, locale, fractionDisplay, true, true);
    const spanStr = formatNumberWithSign(span, alwaysShowSigns, numberType, locale, fractionDisplay, true, true);
    const endStr = formatNumberWithSign(endValue, alwaysShowSigns, numberType, locale, fractionDisplay, true, true);
    
    // Build the label based on toggles
    const parts: string[] = [];
    
    // Show jump value (+3) if toggle is on and calculation is off
    // For additive mode, always show large plus sign before the number AND superscript sign on number
    if (showJumpValue && !showCalculation) {
      // Format span with superscript sign on the number (force alwaysShowSigns=true for superscript)
      const spanWithSuperscript = formatNumberWithSign(span, true, numberType, locale, fractionDisplay, true, true);
      // Add large plus sign before the number (operation sign)
      parts.push(`+${spanWithSuperscript}`);
    }
    
    // Show calculation if toggle is on
    if (showCalculation) {
      parts.push(`${startStr}+${spanStr}`);
    }
    
    // Show result if toggle is on
    if (showResult) {
      if (showCalculation || showJumpValue) {
        parts.push('=');
      }
      parts.push(endStr);
    }
    
    return parts.join(' ');
  }
}

/**
 * Gets the display label for a jump
 */
export function getJumpDisplayLabel(
  jump: Jump,
  settings: JumpLayoutSettings,
  numberType: 'integer' | 'decimal' | 'fraction' | 'percent',
  locale: LocaleCode,
  fractionDisplay?: { format: 'mixed' | 'improper'; simplify: boolean }
): string {
  // If custom label is set and auto-label is disabled, use it
  if (!jump.autoLabel && jump.label) {
    return jump.label;
  }

  const { jumpType, scaleFactorFormat, showCalculation, showResult, showJumpValue, alwaysShowSigns } = settings;
  const startValue = jump.startValue;
  const endValue = jump.endValue;
  const span = endValue - startValue;

  // Check if multiplicative mode is valid (start value must not be zero)
  const canUseMultiplicative = jumpType === 'multiplicative' && Math.abs(startValue) > 1e-10;

  // If multiplicative mode requested but invalid, fall back to additive
  const effectiveJumpType = canUseMultiplicative ? 'multiplicative' : 'additive';

  if (effectiveJumpType === 'multiplicative') {
    // Multiplicative mode: startValue × factor = endValue
    // Calculate the scale factor: factor = endValue / startValue
    const factor = endValue / startValue;
    
    // Format scale factor with sufficient precision to avoid misleading rounding
    // For scale factors, always use decimal or fraction format (not integer) to preserve accuracy
    // If numberType is integer, use decimal instead for the factor to show exact value
    const factorNumberType = numberType === 'integer' ? 'decimal' : numberType;
    const factorStr = formatNumberWithSign(factor, false, factorNumberType, locale, fractionDisplay, false, false);
    // Format start and end values with signs if requested
    const startStr = formatNumberWithSign(startValue, alwaysShowSigns, numberType, locale, fractionDisplay, false, false);
    const endStr = formatNumberWithSign(endValue, alwaysShowSigns, numberType, locale, fractionDisplay, false, false);
    
    // Build the label based on toggles
    if (scaleFactorFormat === 'SF=') {
      // SF= format: show "SF=3" and optionally "1×3=3" on separate line
      const parts: string[] = [];
      
      // Show jump value (SF=3) if toggle is on
      if (showJumpValue) {
        parts.push(`SF=${factorStr}`);
      }
      
      // Show calculation if toggle is on
      if (showCalculation) {
        const calcLine = `${startStr}×${factorStr}${showResult ? `=${endStr}` : ''}`;
        if (showJumpValue) {
          // If jump value is shown, put calculation on new line
          parts.push(`<br/>${calcLine}`);
        } else {
          parts.push(calcLine);
        }
      } else if (showResult && !showJumpValue) {
        // Only show result if calculation is off and jump value is off
        parts.push(`=${endStr}`);
      }
      
      return parts.join('');
    } else {
      // × format: show "×4" or "1×4=6" or just parts
      const parts: string[] = [];
      
      // Show jump value (×4) if toggle is on and calculation is off
      if (showJumpValue && !showCalculation) {
        parts.push(`×${factorStr}`);
      }
      
      // Show calculation if toggle is on
      if (showCalculation) {
        parts.push(`${startStr}×${factorStr}`);
      }
      
      // Show result if toggle is on
      if (showResult) {
        if (showCalculation || (showJumpValue && !showCalculation)) {
          parts.push('=');
        }
        parts.push(endStr);
      }
      
      return parts.join(' ');
    }
  } else {
    // Additive mode: startValue + span = endValue
    const startStr = formatNumberWithSign(startValue, alwaysShowSigns, numberType, locale, fractionDisplay, false, false);
    const spanStr = formatNumberWithSign(span, alwaysShowSigns, numberType, locale, fractionDisplay, false, false);
    const endStr = formatNumberWithSign(endValue, alwaysShowSigns, numberType, locale, fractionDisplay, false, false);
    
    // Build the label based on toggles
    const parts: string[] = [];
    
    // Show jump value (+3) if toggle is on and calculation is off
    // For additive mode, always show large plus sign before the number AND superscript sign on number
    if (showJumpValue && !showCalculation) {
      // Format span with superscript sign on the number (force alwaysShowSigns=true for superscript)
      const spanWithSuperscript = formatNumberWithSign(span, true, numberType, locale, fractionDisplay, false, false);
      // For plain text, we need to add the large plus and handle the superscript differently
      // Since we can't do superscript in plain text easily, we'll show +span with the sign
      // But for consistency, format it as +span where span has its sign
      const sign = span >= 0 ? '+' : '-';
      const absSpan = Math.abs(span);
      const absSpanStr = formatNumberWithSign(absSpan, false, numberType, locale, fractionDisplay, false, false);
      parts.push(`${sign}${absSpanStr}`);
    }
    
    // Show calculation if toggle is on
    if (showCalculation) {
      parts.push(`${startStr}+${spanStr}`);
    }
    
    // Show result if toggle is on
    if (showResult) {
      if (showCalculation || (showJumpValue && !showCalculation)) {
        parts.push('=');
      }
      parts.push(endStr);
    }
    
    return parts.join(' ');
  }
}
